import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import {
  useAppBaseContext,
  useConfiguratorPopupContext,
  useStateLoaderContext,
} from "providers";
import { useAuthStore } from "stores/authStore";
import { useProjectDataStore } from "stores/projectStore";
import { UncontrolledTooltip } from "reactstrap";
import { useTranslate } from "hooks";
import { useFetchProject } from "hooks/projectHooks";
import { useProductStateChanged } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import { tenants } from "config/tenants";
import { lastSelectedUserLangLSKey } from "variables/localStorageItemsNames";
import { Icon } from "../../Icon/Icon";
import { NavRightSidebar } from "./NavRightSidebar";
import { LoginButton } from "../LoginButton";
import { Logo } from "../Logo";

import s from "./navConfigurator.module.scss";

export const NavConfigurator = Scrivito.connect(({ homepage, device }) => {
  const isAuthenticated = useAuthStore.use.isAuthenticated();
  const currentUserTenant = useAuthStore.use.currentUser()?.tenant;

  const translate = useTranslate();
  const projectName = useProjectDataStore((state) => state.projectName);
  const { projectId: projectIdFromUrl } = Scrivito.currentPageParams();
  const urlTitle = new URLSearchParams(window.location.search).get(
    "originalProjectName",
  );
  const title = urlTitle || projectName;

  const projectId = useProjectDataStore((state) => state.projectId);
  const setProjectId = useProjectDataStore((state) => state.setProjectId);
  const resetProjectStore = useProjectDataStore((state) => state.reset);
  const refetchProject = useFetchProject();

  // TODO GET_INITIAL_PROJECT_STATE ANOTHER ONE BUAUTIFUL STUFF
  useEffect(() => {
    if (!projectId && projectIdFromUrl) {
      setProjectId(projectIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isSameProj, lang: selectedLang, setLang } = useAppBaseContext();
  const isUpdateButtonActive = useProductStateChanged();
  const { showConfiguratorPopup } = useConfiguratorPopupContext();
  const { isLoaderLine } = useStateLoaderContext();
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const supportedLanguages = useMemo(
    () => tenants[currentUserTenant || "SC"]?.language || [],
    [currentUserTenant],
  );

  useEffect(() => {
    if (!supportedLanguages.length) {
      return;
    }
    if (!selectedLang) {
      return;
    }

    const allowedValues = supportedLanguages.map((l) => l.value);
    if (allowedValues.includes(selectedLang)) {
      return;
    }

    const tenantKey = currentUserTenant || "SC";
    const fallbackLanguage =
      tenants[tenantKey]?.default_language || supportedLanguages[0]?.value;

    if (!fallbackLanguage || fallbackLanguage === selectedLang) {
      return;
    }

    localStorage.setItem(lastSelectedUserLangLSKey, fallbackLanguage);
    setLang(fallbackLanguage);
  }, [supportedLanguages, selectedLang, currentUserTenant, setLang]);

  const onToggleSideBar = useCallback(() => {
    setSidebarOpen((val) => !val);
  }, []);

  const handleprojectRefetch = useCallback(() => {
    const isWizardPage = window.location.href.includes("wizard");
    if (isWizardPage) {
      refetchProject();
    }
  }, [refetchProject]);

  const goHome = useCallback(() => {
    if (isAuthenticated) {
      if (!homepage) {
        return;
      }
      const home = homepage.get("welcomePage");
      const navigate = () => Scrivito.navigateTo(home);

      resetProjectStore();

      if (isUpdateButtonActive) {
        showConfiguratorPopup(navigate);
      } else {
        navigate();
      }
    }
  }, [
    isAuthenticated,
    homepage,
    resetProjectStore,
    isUpdateButtonActive,
    showConfiguratorPopup,
  ]);

  if (!homepage) {
    return null;
  }

  return (
    <>
      <header
        data-testid="nav-configurator"
        className={cx(
          "py-3",
          s.configuratorHeader,
          s.configuratorHeaderWithGrayBar,
          {
            [s.loader]: isLoaderLine,
          },
        )}
      >
        <div className="d-flex justify-content-between px-4">
          <div className="d-flex align-items-center">
            <Logo
              homepage={homepage}
              imgClass={`m-0 ${s.logoIcon} w-auto me-2 me-md-3 me-xl-4`}
              isConfigLog
            />
            <h3 className="mb-0 text-uppercase text-gray-500">
              {homepage.get("configuratorNavTitle")}
              {!isSameProj && (
                <>
                  <span id="beta" className="text-blue-300 ps-2">
                    BETA
                  </span>
                  <UncontrolledTooltip target="beta" placement="bottom">
                    {translate("TROX_BETA_INFO_TEXT")}
                  </UncontrolledTooltip>
                </>
              )}
            </h3>
            {projectId && title && (
              <div
                className={cx(
                  "d-flex align-items-center mx-2 px-2 border-start border-gray-400",
                )}
              >
                <Scrivito.LinkTag
                  to={homepage.get("projectConfiguratorPage")}
                  params={{ projectId }}
                  className="d-flex flex-no-wrap"
                  onClick={handleprojectRefetch}
                >
                  <Icon
                    prefix="trox"
                    iconName="folder-regular"
                    className="icon-16 text-gray-500 me-2"
                  />

                  <div className="font-bold text-gray-500">{title}</div>
                </Scrivito.LinkTag>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            {!isSameProj && (
              <div className="position-relative me-6">
                {/* TODO: Rows below are for future when a shop there's in configurator app */}
                {/* <div */}
                {/*  className={cx( */}
                {/*    ` */}
                {/*  position-absolute */}
                {/*  d-flex */}
                {/*  justify-content-center */}
                {/*  align-items-center */}
                {/*  w-20 */}
                {/*  h-20 */}
                {/*  rounded-circle */}
                {/*  bg-blue-300 */}
                {/* `, */}
                {/*    s.cartBubble */}
                {/*  )} */}
                {/* > */}
                {/*  <span className={cx("d-flex align-items-center text-white", s.cartNumber)}>18</span> */}
                {/* </div> */}
                <button
                  data-testid="NavConfigurator_shopping-cart-btn"
                  className="p-0 bg-transparent border-0"
                >
                  <a
                    className="text-decoration-none"
                    href="https://shop.trox.de/"
                  >
                    {/* TODO: forthcoming feature (hidden acc US-19953) hidden with className='d-none' */}
                    <Icon
                      prefix="trox"
                      iconName="shopping-cart-light"
                      className="icon-32 text-blue-800 d-none"
                      title="shopping-cart"
                    />
                  </a>
                </button>
              </div>
            )}
            {device !== "mobile" && (
              <>
                <LoginButton />
                {!isSameProj && (
                  <>
                    <Scrivito.LinkTag
                      onClick={goHome}
                      className="bg-transparent p-0 text-decoration-none ms-md-4 ms-xl-6"
                      data-testid="home-icon"
                    >
                      <Icon
                        prefix="trox"
                        iconName="home-header"
                        className="icon-32"
                        title="home"
                        color="white"
                      />
                    </Scrivito.LinkTag>

                    <button
                      data-testid="hamburger-icon"
                      onClick={onToggleSideBar}
                      className="bg-transparent border-0 p-0 ms-4"
                      title="bars-button"
                    >
                      <Icon
                        prefix="trox"
                        iconName="bars-regular"
                        className="icon-32 text-gray-400"
                        title="bars"
                      />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </header>
      {isSidebarOpen && (
        <NavRightSidebar
          onToggleSideBar={onToggleSideBar}
          isOpen={isSidebarOpen}
        />
      )}
    </>
  );
});
